import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef, useRef } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE } from '../config';
import { useLocale } from '../contexts/LocaleContext';
import { useToast } from '../contexts/ToastContext';
import { readJsonWithOnboarding } from '../onboarding/notify-onboarding-update';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { NoteFormDialog } from './NoteFormDialog';
import { NoteCard } from './NoteCard';
import { NotesBulkActions } from './NotesBulkActions';
import { SelectableNoteRow } from './SelectableNoteRow';
import { NotesEmptyState, NotesLoadingState, notesListContainerClassName, NotesPwaShortcutHint } from './NotesListLayout';
import type { MentionableEntity } from './RichTextField';
import { wouldExceedStorageQuota } from '../utils/storage-quota';
import { USER_NOTES_LIMIT } from '../constants';
import { stripHtml, NOTES_CONTENT_MAX_LENGTH } from '../utils/note-utils';
import { downloadNoteAttachment } from '../utils/note-attachment-download';
import { uploadNoteFileAsProjectResource } from '../utils/note-project-attachment';
import { mapMentionableEntitiesFromApiResponse } from '../utils/mentionable-entities';
import {
  fetchAssignableProjects,
  selectRecentAssignableProjects,
  type AssignableProject,
} from '../utils/recent-assignable-projects';
import { notifyUserNotesChanged } from '../utils/user-notes-events';
import { notifyProjectNotesChanged } from '../utils/project-notes-events';
import { useShiftClickRowSelection } from '../hooks/useShiftClickRowSelection';

const userNotesLimitReachedMessage = (translate: (key: string) => string): string =>
  (translate('user_notes_limit_reached') || 'You have reached the maximum of {limit} notes. Move some to a project to free space.')
    .replace('{limit}', String(USER_NOTES_LIMIT));

export interface UserNote {
  id: string;
  ownerId: string;
  projectId: string | null;
  content: string;
  attachmentS3Key: string | null;
  attachmentFileName: string | null;
  attachmentFileSize: number | null;
  attachmentMimeType: string | null;
  attachmentResourceId: string | null;
  createdAt: string;
  updatedAt: string;
}

interface UserNotesProps {
  token: string | null;
  notes: UserNote[];
  loading: boolean;
  onRefresh: () => void;
  initialOpenCreate?: boolean;
}

export interface UserNotesHandle {
  openCreate: () => void;
}

export const UserNotes = forwardRef<UserNotesHandle, UserNotesProps>(({
  token,
  notes,
  loading,
  onRefresh,
  initialOpenCreate = false,
}, ref) => {
  const { t } = useLocale();
  const { showToast, showErrorToast, hideToast } = useToast();
  const [editNoteId, setEditNoteId] = useState<string | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(initialOpenCreate);
  const [createContent, setCreateContent] = useState('');
  const [createFile, setCreateFile] = useState<File | null>(null);
  const [editContent, setEditContent] = useState('');
  const [editFile, setEditFile] = useState<File | null>(null);
  const [moveNoteIds, setMoveNoteIds] = useState<string[]>([]);
  const [projects, setProjects] = useState<AssignableProject[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [mentionableEntities, setMentionableEntities] = useState<MentionableEntity[]>([]);
  const [saving, setSaving] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [bulkActing, setBulkActing] = useState(false);
  const getVisibleNoteIdsRef = useRef<() => string[]>(() => notes.map((note) => note.id));
  getVisibleNoteIdsRef.current = () => notes.map((note) => note.id);
  const {
    selectedIds: selectedNoteIds,
    setRowSelection,
    selectRangeTo,
    toggleRowSelected,
    clearSelection,
    setAnchorRowId,
  } = useShiftClickRowSelection({
    getVisibleRowIds: () => getVisibleNoteIdsRef.current(),
  });

  const handleSelectAllChange = (checked: boolean): void => {
    const visibleIds = getVisibleNoteIdsRef.current();
    if (!checked) {
      clearSelection();
      return;
    }
    const next: Record<string, boolean> = {};
    for (const id of visibleIds) {
      next[id] = true;
    }
    setRowSelection(next);
    setAnchorRowId(visibleIds.length > 0 ? visibleIds[0] : null);
  };

  const editingNote = editNoteId ? notes.find((n) => n.id === editNoteId) : null;
  const recentProjects = selectRecentAssignableProjects(projects);
  const noteFormOpen = isCreateOpen || Boolean(editingNote);
  const moveDialogOpen = moveNoteIds.length > 0;

  useEffect(() => {
    if (editingNote) {
      setEditContent(editingNote.content);
      setEditFile(null);
      setSelectedProjectId(null);
    }
  }, [editingNote]);

  const createContentLength = stripHtml(createContent).length;
  const editContentLength = stripHtml(editContent).length;
  const createContentOk = createContentLength <= NOTES_CONTENT_MAX_LENGTH;
  const editContentOk = editContentLength <= NOTES_CONTENT_MAX_LENGTH;

  const loadAssignableProjects = useCallback(async (): Promise<void> => {
    if (!token) return;
    setProjectsLoading(true);
    try {
      const assignable = await fetchAssignableProjects(token);
      setProjects(assignable);
    } catch {
      setProjects([]);
    } finally {
      setProjectsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (moveDialogOpen || noteFormOpen) {
      void loadAssignableProjects();
    }
  }, [moveDialogOpen, noteFormOpen, loadAssignableProjects]);

  useEffect(() => {
    if (!token || !selectedProjectId || !noteFormOpen) {
      setMentionableEntities([]);
      return;
    }
    let cancelled = false;
    const fetchMentionable = async (): Promise<void> => {
      try {
        const res = await fetch(`${API_BASE}/api/projects/${selectedProjectId}/mentionable-entities`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          if (!cancelled) setMentionableEntities([]);
          return;
        }
        const data = await res.json();
        if (!cancelled) {
          setMentionableEntities(mapMentionableEntitiesFromApiResponse(data));
        }
      } catch {
        if (!cancelled) setMentionableEntities([]);
      }
    };
    void fetchMentionable();
    return () => {
      cancelled = true;
    };
  }, [token, selectedProjectId, noteFormOpen]);

  useEffect(() => {
    if (initialOpenCreate) {
      setIsCreateOpen(true);
    }
  }, [initialOpenCreate]);

  const handleCreateOpen = useCallback((): void => {
    setCreateContent('');
    setCreateFile(null);
    setSelectedProjectId(null);
    setIsCreateOpen(true);
  }, []);

  useImperativeHandle(ref, () => ({
    openCreate: handleCreateOpen,
  }), [handleCreateOpen]);

  const showMovedToast = (projectId: string, projectTitle: string): void => {
    const message = (t('note_moved_to_project_toast') || 'The note has been moved to {projectTitle} project.').replace(
      '{projectTitle}',
      projectTitle
    );
    showToast(
      <>
        {message}
        {' '}
        <Link
          to={`/projects/${projectId}/notes`}
          style={{ color: 'inherit', textDecoration: 'underline', fontWeight: 500 }}
          onClick={() => hideToast()}
        >
          {t('open_notes') || 'Open notes'}
        </Link>
      </>
    );
  };

  const showCreatedInProjectToast = (projectId: string, noteId: string): void => {
    showToast(
      <>
        {t('note_created_toast_suffix_before')}
        <Link
          to={`/projects/${projectId}/notes?noteId=${encodeURIComponent(noteId)}`}
          style={{ color: 'inherit', textDecoration: 'underline', fontWeight: 600 }}
          onClick={() => hideToast()}
        >
          {t('note_created_toast_link_text')}
        </Link>
        {t('note_created_toast_suffix_after')}
      </>,
    );
  };

  const moveNotesToProject = async (
    noteIds: string[],
    projectId: string,
    projectTitle: string
  ): Promise<void> => {
    if (!token || noteIds.length === 0) return;
    for (const noteId of noteIds) {
      const res = await fetch(`${API_BASE}/api/notes/${noteId}/move`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ projectId }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to move note');
      }
      await readJsonWithOnboarding(res);
    }
    onRefresh();
    notifyUserNotesChanged();
    notifyProjectNotesChanged(projectId);
    setMoveNoteIds([]);
    setEditNoteId(null);
    clearSelection();
    if (noteIds.length === 1) {
      showMovedToast(projectId, projectTitle);
    } else {
      const message = (t('notes_moved_to_project_toast') || '{count} notes have been moved to {projectTitle} project.')
        .replace('{count}', String(noteIds.length))
        .replace('{projectTitle}', projectTitle);
      showToast(
        <>
          {message}
          {' '}
          <Link
            to={`/projects/${projectId}/notes`}
            style={{ color: 'inherit', textDecoration: 'underline', fontWeight: 500 }}
            onClick={() => hideToast()}
          >
            {t('open_notes') || 'Open notes'}
          </Link>
        </>
      );
    }
  };

  const handleCreateSave = async (): Promise<void> => {
    if (!token || !createContentOk) return;
    setSaving(true);
    try {
      if (selectedProjectId) {
        let attachmentResourceId: string | null = null;
        if (createFile) {
          setUploadingAttachment(true);
          try {
            attachmentResourceId = await uploadNoteFileAsProjectResource(token, selectedProjectId, createFile);
          } catch (e) {
            if (e instanceof Error && e.message === 'STORAGE_LIMIT_REACHED') {
              return;
            }
            throw e;
          } finally {
            setUploadingAttachment(false);
          }
        }
        const res = await fetch(`${API_BASE}/api/projects/${selectedProjectId}/notes`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            content: createContent,
            ...(attachmentResourceId && { attachmentResourceId }),
          }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || 'Failed to create note');
        }
        const data = await readJsonWithOnboarding<{ note: { id: string } }>(res);
        onRefresh();
        notifyUserNotesChanged();
        notifyProjectNotesChanged(selectedProjectId);
        setIsCreateOpen(false);
        setCreateContent('');
        setCreateFile(null);
        setSelectedProjectId(null);
        showCreatedInProjectToast(selectedProjectId, data.note.id);
        return;
      }

      let attachmentS3Key: string | undefined;
      let attachmentFileName: string | undefined;
      let attachmentFileSize: number | undefined;
      let attachmentMimeType: string | undefined;
      if (createFile) {
        const exceedsQuota = await wouldExceedStorageQuota(token, createFile.size);
        if (exceedsQuota) {
          window.dispatchEvent(new CustomEvent('storage-limit-reached'));
          return;
        }
        const presignedRes = await fetch(`${API_BASE}/api/notes/presigned-url`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            fileName: createFile.name,
            fileSize: createFile.size,
            mimeType: createFile.type || 'application/octet-stream',
          }),
        });
        if (!presignedRes.ok) {
          const err = await presignedRes.json().catch(() => ({}));
          throw new Error(err.error || 'Failed to get upload URL');
        }
        const { presignedUrl, s3Key } = await presignedRes.json();
        const uploadRes = await fetch(presignedUrl, {
          method: 'PUT',
          headers: { 'Content-Type': createFile.type || 'application/octet-stream' },
          body: createFile,
        });
        if (!uploadRes.ok) throw new Error('Upload failed');
        attachmentS3Key = s3Key;
        attachmentFileName = createFile.name;
        attachmentFileSize = createFile.size;
        attachmentMimeType = createFile.type || 'application/octet-stream';
      }
      const res = await fetch(`${API_BASE}/api/notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          content: createContent,
          ...(attachmentS3Key && {
            attachmentS3Key,
            attachmentFileName,
            attachmentFileSize,
            attachmentMimeType,
          }),
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        if (err.errorCode === 'USER_NOTES_LIMIT_REACHED') {
          showErrorToast(userNotesLimitReachedMessage(t));
          return;
        }
        throw new Error(err.error || 'Failed to create note');
      }
      await readJsonWithOnboarding(res);
      onRefresh();
      notifyUserNotesChanged();
      setIsCreateOpen(false);
      setCreateContent('');
      setCreateFile(null);
      setSelectedProjectId(null);
      showToast(t('note_created') || 'Note created');
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to create note');
    } finally {
      setSaving(false);
      setUploadingAttachment(false);
    }
  };

  const handleEditSave = async (): Promise<void> => {
    if (!token || !editingNote || !editContentOk) return;
    setSaving(true);
    try {
      let attachmentS3Key: string | null = editingNote.attachmentS3Key;
      let attachmentFileName: string | null = editingNote.attachmentFileName;
      let attachmentFileSize: number | null = editingNote.attachmentFileSize;
      let attachmentMimeType: string | null = editingNote.attachmentMimeType;
      if (editFile) {
        const exceedsQuota = await wouldExceedStorageQuota(token, editFile.size);
        if (exceedsQuota) {
          window.dispatchEvent(new CustomEvent('storage-limit-reached'));
          return;
        }
        const presignedRes = await fetch(`${API_BASE}/api/notes/presigned-url`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            fileName: editFile.name,
            fileSize: editFile.size,
            mimeType: editFile.type || 'application/octet-stream',
          }),
        });
        if (!presignedRes.ok) {
          const err = await presignedRes.json().catch(() => ({}));
          throw new Error(err.error || 'Failed to get upload URL');
        }
        const { presignedUrl, s3Key } = await presignedRes.json();
        const uploadRes = await fetch(presignedUrl, {
          method: 'PUT',
          headers: { 'Content-Type': editFile.type || 'application/octet-stream' },
          body: editFile,
        });
        if (!uploadRes.ok) throw new Error('Upload failed');
        attachmentS3Key = s3Key;
        attachmentFileName = editFile.name;
        attachmentFileSize = editFile.size;
        attachmentMimeType = editFile.type || 'application/octet-stream';
      }
      const res = await fetch(`${API_BASE}/api/notes/${editingNote.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          content: editContent,
          attachmentS3Key,
          attachmentFileName,
          attachmentFileSize,
          attachmentMimeType,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to update note');
      }
      if (selectedProjectId) {
        const project = projects.find((p) => p.id === selectedProjectId);
        await moveNotesToProject(
          [editingNote.id],
          selectedProjectId,
          project?.title || t('project_title') || 'Project'
        );
        setSelectedProjectId(null);
        return;
      }
      onRefresh();
      setEditNoteId(null);
      setSelectedProjectId(null);
      showToast(t('note_updated') || 'Note updated');
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to update note');
    } finally {
      setSaving(false);
    }
  };

  const showTrashToast = (): void => {
    showToast(
      <>
        {t('note_moved_to') || 'Note moved to '}
        <Link
          to="/desk/trash"
          style={{
            color: '#ffffff',
            textDecoration: 'underline',
            fontWeight: 500,
          }}
        >
          {t('trash')}
        </Link>
      </>
    );
  };

  const deleteNotes = async (noteIds: string[]): Promise<void> => {
    if (!token || noteIds.length === 0) return;
    for (const noteId of noteIds) {
      const res = await fetch(`${API_BASE}/api/notes/${noteId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to delete note');
    }
    onRefresh();
    notifyUserNotesChanged();
    if (editNoteId && noteIds.includes(editNoteId)) {
      setEditNoteId(null);
    }
    clearSelection();
    showTrashToast();
  };

  const handleDelete = async (noteId: string): Promise<void> => {
    try {
      await deleteNotes([noteId]);
    } catch {
      showErrorToast(t('failed_to_delete_note') || 'Failed to move note to trash');
    }
  };

  const handleBulkMoveToTrash = async (): Promise<void> => {
    if (selectedNoteIds.length === 0 || bulkActing) return;
    setBulkActing(true);
    try {
      await deleteNotes(selectedNoteIds);
    } catch {
      showErrorToast(t('failed_to_delete_note') || 'Failed to move note to trash');
    } finally {
      setBulkActing(false);
    }
  };

  const handleDownloadAttachment = (note: UserNote): void => {
    if (!token || !note.attachmentFileName) return;
    downloadNoteAttachment(
      API_BASE,
      token,
      { type: 'user', noteId: note.id },
      (msg) => showErrorToast(msg)
    );
  };

  const handleMove = async (projectId: string, projectTitle: string): Promise<void> => {
    setBulkActing(true);
    try {
      await moveNotesToProject(moveNoteIds, projectId, projectTitle);
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to move note');
    } finally {
      setBulkActing(false);
    }
  };

  return (
    <>
      {loading ? (
        <NotesLoadingState />
      ) : (
        <div className={notesListContainerClassName}>
          {notes.length >= USER_NOTES_LIMIT && (
            <p style={{ fontSize: '0.875rem', color: 'var(--muted-foreground)' }}>
              {userNotesLimitReachedMessage(t)}
            </p>
          )}
          {notes.length === 0 ? (
            <NotesEmptyState canEdit={Boolean(token)} onAdd={handleCreateOpen} />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <p className="text-sm text-muted-foreground">
                {t('desk_notes_assign_to_project_hint').replace('{count}', String(notes.length))}
              </p>
              <NotesBulkActions
                selectedCount={selectedNoteIds.length}
                totalCount={notes.length}
                disabled={bulkActing}
                onSelectAllChange={handleSelectAllChange}
                onMoveToProject={() => setMoveNoteIds([...selectedNoteIds])}
                onMoveToTrash={() => {
                  void handleBulkMoveToTrash();
                }}
              />
              {notes.map((note) => (
                <SelectableNoteRow
                  key={note.id}
                  noteId={note.id}
                  selected={selectedNoteIds.includes(note.id)}
                  ariaLabel={t('select_note')}
                  onSelectRange={selectRangeTo}
                  onToggle={toggleRowSelected}
                >
                  <NoteCard
                    note={note}
                    selected={selectedNoteIds.includes(note.id)}
                    onEditClick={() => {
                      setEditContent(note.content);
                      setEditFile(null);
                      setEditNoteId(note.id);
                    }}
                    onDownloadAttachment={
                      note.attachmentFileName ? () => handleDownloadAttachment(note) : undefined
                    }
                    onMoveToProject={() => setMoveNoteIds([note.id])}
                    onMoveToTrash={() => {
                      void handleDelete(note.id);
                    }}
                  />
                </SelectableNoteRow>
              ))}
              <NotesPwaShortcutHint />
            </div>
          )}
        </div>
      )}

      <NoteFormDialog
        open={isCreateOpen}
        onOpenChange={(open) => {
          setIsCreateOpen(open);
          if (!open) {
            setSelectedProjectId(null);
          }
        }}
        mode="create"
        content={createContent}
        onContentChange={setCreateContent}
        file={createFile}
        onFileChange={setCreateFile}
        mentionableEntities={mentionableEntities}
        contentLength={createContentLength}
        maxLength={NOTES_CONTENT_MAX_LENGTH}
        contentOk={createContentOk}
        saving={saving}
        uploadingAttachment={uploadingAttachment}
        projectOptions={recentProjects}
        selectedProjectId={selectedProjectId}
        onSelectedProjectIdChange={setSelectedProjectId}
        onSave={() => {
          void handleCreateSave();
        }}
        onCancel={() => {
          setIsCreateOpen(false);
          setSelectedProjectId(null);
        }}
      />

      {editingNote && (
        <NoteFormDialog
          open={!!editingNote}
          onOpenChange={(open) => {
            if (!open) {
              setEditNoteId(null);
              setSelectedProjectId(null);
            }
          }}
          mode="edit"
          fieldKey={editingNote.id}
          content={editContent}
          onContentChange={setEditContent}
          file={editFile}
          onFileChange={setEditFile}
          existingAttachmentFileName={editingNote.attachmentFileName}
          onDownloadExistingAttachment={
            editingNote.attachmentFileName
              ? () => handleDownloadAttachment(editingNote)
              : undefined
          }
          mentionableEntities={mentionableEntities}
          contentLength={editContentLength}
          maxLength={NOTES_CONTENT_MAX_LENGTH}
          contentOk={editContentOk}
          saving={saving}
          createdAt={editingNote.createdAt}
          projectOptions={recentProjects}
          selectedProjectId={selectedProjectId}
          onSelectedProjectIdChange={setSelectedProjectId}
          onDelete={() => {
            void handleDelete(editingNote.id);
            setEditNoteId(null);
            setSelectedProjectId(null);
          }}
          onMoveToProject={() => {
            setEditNoteId(null);
            setSelectedProjectId(null);
            setMoveNoteIds([editingNote.id]);
          }}
          onSave={() => {
            void handleEditSave();
          }}
          onCancel={() => {
            setEditNoteId(null);
            setSelectedProjectId(null);
          }}
        />
      )}

      <Dialog
        open={moveDialogOpen}
        onOpenChange={(open) => {
          if (!open) setMoveNoteIds([]);
        }}
      >
        <DialogContent onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>{t('move_to_project') || 'Move to project'}</DialogTitle>
          </DialogHeader>
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {projectsLoading ? (
              <div>{t('loading')}</div>
            ) : projects.length === 0 ? (
              <p style={{ fontSize: '0.875rem', color: 'var(--muted-foreground)' }}>{t('no_projects') || 'No projects'}</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                {projects.map((p) => (
                  <Button
                    key={p.id}
                    variant="ghost"
                    className="justify-start"
                    disabled={bulkActing}
                    onClick={() => {
                      void handleMove(p.id, p.title);
                    }}
                  >
                    {p.title}
                  </Button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
});

UserNotes.displayName = 'UserNotes';
